<template>
    <basic-header pretitle="aqfer Graph Management" title="Graph Analytics Report" :compact="true" />
    <div class="container-fluid">
        <div class="row g-3 align-items-center mt-3 mb-2">
            <div class="d-flex justify-content-between align-items-center">
                <div class="d-flex gap-3">
                    <div class="col-auto">
                        <InputField colField="mb-0" :editable="true" label="Data Source" :options="dataSourceOptions"
                            type="select" :modelValue="dataSource" @update:modelValue="dataSource = $event" />
                    </div>
                    <div class="col-auto">
                        <InputField label="Start Date" colField="mb-0" :modelValue="startDate" type="calendar"
                            :input-config="dateConfig" @update:modelValue="startDate = $event" />
                    </div>
                    <div class="col-auto">
                        <InputField label="End Date" colField="mb-0" :modelValue="endDate" type="calendar"
                            :input-config="dateConfig" @update:modelValue="endDate = $event" />
                    </div>
                </div>
                <div class="col-auto ms-auto mt-5">
                    <div class="dropdown">
                        <button class="btn btn-primary btn-sm dropdown-toggle" type="button" data-bs-toggle="dropdown"
                            aria-expanded="false">
                            Export
                        </button>
                        <ul class="dropdown-menu">
                            <li><a class="dropdown-item" href="#" @click="exportCSV()">Current View (CSV)</a></li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
        <Tabs :tabs="tabItems">
            <template v-slot:top_n_entities>
                <div class="card">
                    <div class="card-body" :class="{ 'table-card-body': topNentitiesReportData?.length }">
                        <div v-if="topNentitiesLoading" class="text-center">
                            <div class="spinner-border spinner-border-sm" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                        </div>
                        <div v-else-if="topNentitiesReportData?.length" class="table-container">
                            <CollapsibleTable id="graph-report-table1" :data="topNentitiesReportData"
                                :columns="topNentitiesReportColumns" :groupBy="topNentitiesTableGroupBy"
                                :aggregateCol="topNentitiesTableAggregateCol"
                                :collapsibleCol="topNentitiesTableCollapsibleCol" />
                        </div>
                        <div v-else class="text-center text-muted">
                            No data available for the selected criteria.
                        </div>
                    </div>
                </div>
                <div class="card">
                    <div class="card-body">
                        <div v-if="topNentitiesLoading" class="text-center">
                            <div class="spinner-border spinner-border-sm" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                        </div>
                        <div v-else-if="topNentitiesReportData?.length" class="row">
                            <div class="col-12">
                                <div v-for="(chartConfig, graphName) in topNentitiesChartData" :key="graphName"
                                    class="mb-5 chart-view">
                                    <h4 class="text-center">{{ graphName }} - Top N Entities</h4>
                                    <Line :data="chartConfig" :options="chartOptions" />
                                </div>
                            </div>
                        </div>
                        <div v-else class="text-center text-muted">
                            No chart data available for the selected criteria.
                        </div>
                    </div>
                </div>
            </template>
            <template v-slot:histogram>
                <div class="card">
                    <div class="card-body" :class="{ 'table-card-body': histogramReportData?.length }">
                        <div v-if="histogramLoading" class="text-center">
                            <div class="spinner-border spinner-border-sm" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                        </div>
                        <div v-else-if="histogramReportData?.length" class="table-container">
                            <CollapsibleTable id="graph-report-table2" :data="histogramReportData"
                                :columns="histogramReportColumns" :groupBy="histogramTableGroupBy"
                                :aggregateCol="histogramTableAggregateCol"
                                :collapsibleCol="histogramTableCollapsibleCol" />
                        </div>
                        <div v-else class="text-center text-muted">
                            No data available for the selected criteria.
                        </div>
                    </div>
                </div>
                <div class="card">
                    <div class="card-body">
                        <div v-if="histogramLoading" class="text-center">
                            <div class="spinner-border spinner-border-sm" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                        </div>
                        <div v-else-if="histogramReportData?.length" class="row">
                            <div class="col-12">
                                <div v-for="(chartConfig, graphName) in histogramChartData" :key="graphName"
                                    class="mb-5 chart-view">
                                    <h4 class="text-center">{{ graphName }} - Histogram</h4>
                                    <Line :data="chartConfig" :options="chartOptions" />
                                </div>
                            </div>
                        </div>
                        <div v-else class="text-center text-muted">
                            No chart data available for the selected criteria.
                        </div>
                    </div>
                </div>
            </template>
            <template v-slot:histogram_per_type>
                <div class="card">
                    <div class="card-body" :class="{ 'table-card-body': histogramPerTypeReportData?.length }">
                        <div v-if="histogramPerTypeLoading" class="text-center">
                            <div class="spinner-border spinner-border-sm" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                        </div>
                        <div v-else-if="histogramPerTypeReportData?.length" class="table-container">
                            <CollapsibleTable id="graph-report-table3" :data="histogramPerTypeReportData"
                                :columns="histogramPerTypeReportColumns" :groupBy="histogramPerTypeTableGroupBy"
                                :aggregateCol="histogramPerTypeTableAggregateCol"
                                :collapsibleCol="histogramPerTypeTableCollapsibleCol" />
                        </div>
                        <div v-else class="text-center text-muted">
                            No data available for the selected criteria.
                        </div>
                    </div>
                </div>
                <div class="card">
                    <div class="card-body">
                        <div v-if="histogramPerTypeLoading" class="text-center">
                            <div class="spinner-border spinner-border-sm" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                        </div>
                        <div v-else-if="histogramPerTypeReportData?.length" class="row">
                            <div class="col-12">
                                <div v-for="(chartConfig, graphName) in histogramPerTypeChartData" :key="graphName"
                                    class="mb-5 chart-view">
                                    <h4 class="text-center">{{ graphName }} - Histogram Per Type</h4>
                                    <Line :data="chartConfig" :options="chartOptions" />
                                </div>
                            </div>
                        </div>
                        <div v-else class="text-center text-muted">
                            No chart data available for the selected criteria.
                        </div>
                    </div>
                </div>
            </template>
            <template v-slot:edges_summary>
                <div class="card">
                    <div class="card-body" :class="{ 'table-card-body': edgesSummaryReportData?.length }">
                        <div v-if="edgesSummaryLoading" class="text-center">
                            <div class="spinner-border spinner-border-sm" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                        </div>
                        <div v-else-if="edgesSummaryReportData?.length" class="table-container">
                            <CollapsibleTable id="graph-report-table4" :data="edgesSummaryReportData"
                                :columns="edgesSummaryReportColumns" :groupBy="edgesSummaryTableGroupBy"
                                :aggregateCol="edgesSummaryTableAggregateCol"
                                :collapsibleCol="edgesSummaryTableCollapsibleCol" />
                        </div>
                        <div v-else class="text-center text-muted">
                            No data available for the selected criteria.
                        </div>
                    </div>
                </div>
                <div class="card">
                    <div class="card-body">
                        <div v-if="edgesSummaryLoading" class="text-center">
                            <div class="spinner-border spinner-border-sm" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                        </div>
                        <div v-else-if="edgesSummaryReportData?.length" class="row">
                            <div class="col-12">
                                <div v-for="(chartConfig, graphName) in edgesSummaryChartData" :key="graphName"
                                    class="mb-5 chart-view">
                                    <h4 class="text-center">{{ graphName }} - Edges Summary</h4>
                                    <Line :data="chartConfig" :options="chartOptions" />
                                </div>
                            </div>
                        </div>
                        <div v-else class="text-center text-muted">
                            No chart data available for the selected criteria.
                        </div>
                    </div>
                </div>
            </template>
            <template v-slot:entities_summary>
                <div class="card">
                    <div class="card-body" :class="{ 'table-card-body': entitiesSummaryReportData?.length }">
                        <div v-if="entitiesSummaryLoading" class="text-center">
                            <div class="spinner-border spinner-border-sm" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                        </div>
                        <div v-else-if="entitiesSummaryReportData?.length" class="table-container">
                            <CollapsibleTable id="graph-report-table5" :data="entitiesSummaryReportData"
                                :columns="entitiesSummaryReportColumns" :groupBy="entitiesSummaryTableGroupBy"
                                :aggregateCol="entitiesSummaryTableAggregateCol"
                                :collapsibleCol="entitiesSummaryTableCollapsibleCol" />
                        </div>
                        <div v-else class="text-center text-muted">
                            No data available for the selected criteria.
                        </div>
                    </div>
                </div>
                <div class="card">
                    <div class="card-body">
                        <div v-if="entitiesSummaryLoading" class="text-center">
                            <div class="spinner-border spinner-border-sm" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                        </div>
                        <div v-else-if="entitiesSummaryReportData?.length" class="row">
                            <div class="col-12">
                                <div v-for="(chartConfig, graphName) in entitiesSummaryChartData" :key="graphName"
                                    class="mb-5 chart-view">
                                    <h4 class="text-center">{{ graphName }} - Entities Summary</h4>
                                    <Line :data="chartConfig" :options="chartOptions" />
                                </div>
                            </div>
                        </div>
                        <div v-else class="text-center text-muted">
                            No chart data available for the selected criteria.
                        </div>
                    </div>
                </div>
            </template>
            <template v-slot:graph_diff>
                <div class="card">
                    <div class="card-body" :class="{ 'table-card-body': graphDiffReportData?.length }">
                        <div v-if="graphDiffLoading" class="text-center">
                            <div class="spinner-border spinner-border-sm" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                        </div>
                        <div v-else-if="graphDiffReportData?.length" class="table-container">
                            <CollapsibleTable id="graph-report-table6" :data="graphDiffReportData"
                                :columns="graphDiffReportColumns" :groupBy="graphDiffTableGroupBy"
                                :aggregateCol="graphDiffTableAggregateCol"
                                :collapsibleCol="graphDiffTableCollapsibleCol" />
                        </div>
                        <div v-else class="text-center text-muted">
                            No data available for the selected criteria.
                        </div>
                    </div>
                </div>
                <div class="card">
                    <div class="card-body">
                        <!-- <div v-if="graphDiffLoading" class="text-center">
                            <div class="spinner-border spinner-border-sm" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                        </div>
                        <div v-else-if="graphDiffReportData?.length" class="row">
                            <div class="col-12">
                                <div v-for="(chartConfig, graphName) in graphDiffChartData" :key="graphName"
                                    class="mb-5 chart-view">
                                    <h4 class="text-center">{{ graphName }} - Graph Diff</h4>
                                    <Line :data="chartConfig" :options="chartOptions" />
                                </div>
                            </div>
                        </div>
                        <div v-else class="text-center text-muted">
                            No data available for the selected criteria.
                        </div> -->
                        <div class="text-center text-muted">
                            No chart data available for the selected criteria.
                        </div>
                    </div>
                </div>
            </template>
            <template v-slot:expired_entities>
                <div class="card">
                    <div class="card-body" :class="{ 'table-card-body': expiredEntitiesReportData?.length }">
                        <div v-if="expiredEntitiesLoading" class="text-center">
                            <div class="spinner-border spinner-border-sm" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                        </div>
                        <div v-else-if="expiredEntitiesReportData?.length" class="table-container">
                            <CollapsibleTable id="graph-report-table7" :data="expiredEntitiesReportData"
                                :columns="expiredEntitiesReportColumns" :groupBy="expiredEntitiesTableGroupBy"
                                :aggregateCol="expiredEntitiesTableAggregateCol"
                                :collapsibleCol="expiredEntitiesTableCollapsibleCol" />
                        </div>
                        <div v-else class="text-center text-muted">
                            No data available for the selected criteria.
                        </div>
                    </div>
                </div>
                <div class="card">
                    <div class="card-body">
                        <div v-if="expiredEntitiesLoading" class="text-center">
                            <div class="spinner-border spinner-border-sm" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                        </div>
                        <div v-else-if="expiredEntitiesReportData?.length" class="row">
                            <div class="col-12">
                                <div v-for="(chartConfig, graphName) in expiredEntitiesChartData" :key="graphName"
                                    class="mb-5 chart-view">
                                    <h4 class="text-center">{{ graphName }} - Expired Entities</h4>
                                    <Line :data="chartConfig" :options="chartOptions" />
                                </div>
                            </div>
                        </div>
                        <div v-else class="text-center text-muted">
                            No chart data available for the selected criteria.
                        </div>
                    </div>
                </div>
            </template>
            <template v-slot:expired_edges>
                <div class="card">
                    <div class="card-body" :class="{ 'table-card-body': expiredEdgesReportData?.length }">
                        <div v-if="expiredEdgesLoading" class="text-center">
                            <div class="spinner-border spinner-border-sm" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                        </div>
                        <div v-else-if="expiredEdgesReportData?.length" class="table-container">
                            <CollapsibleTable id="graph-report-table8" :data="expiredEdgesReportData"
                                :columns="expiredEdgesReportColumns" :groupBy="expiredEdgesTableGroupBy"
                                :aggregateCol="expiredEdgesTableAggregateCol"
                                :collapsibleCol="expiredEdgesTableCollapsibleCol" />
                        </div>
                        <div v-else class="text-center text-muted">
                            No data available for the selected criteria.
                        </div>
                    </div>
                </div>
                <div class="card">
                    <div class="card-body">
                        <div v-if="expiredEdgesLoading" class="text-center">
                            <div class="spinner-border spinner-border-sm" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                        </div>
                        <div v-else-if="expiredEdgesReportData?.length" class="row">
                            <div class="col-12">
                                <div v-for="(chartConfig, graphName) in expiredEdgesChartData" :key="graphName"
                                    class="mb-5 chart-view">
                                    <h4 class="text-center">{{ graphName }} - Expired Entities</h4>
                                    <Line :data="chartConfig" :options="chartOptions" />
                                </div>
                            </div>
                        </div>
                        <div v-else class="text-center text-muted">
                            No chart data available for the selected criteria.
                        </div>
                    </div>
                </div>
            </template>
        </Tabs>
    </div>
</template>
<script>
import BasicHeader from '../../components/BasicHeader.vue'
import InputField from "../../components/InputField.vue";
import Tabs from "../../components/Tabs.vue";
import CollapsibleTable from "../../components/CollapsibleTable.vue";
import csv from "../../utils/tools/csv"
import { getAgmClientOverrides } from "../../utils/agmClientOverrides";
import dayjs from "dayjs";
import axios from 'axios';
import { mapActions, mapState } from "vuex";
import { Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale, Colors, LineElement, PointElement } from 'chart.js'
import { Line } from 'vue-chartjs'
ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale, Colors, LineElement, PointElement)
export default {
    components: { BasicHeader, InputField, Tabs, CollapsibleTable, Line },
    data() {
        const cid = this.$route.params.cid || this.$store.state.currentClient?.cid;
        const overrides = getAgmClientOverrides(cid);
        const defaultDates = overrides?.graphReportDefaultDates;
        const allSources = [
            { label: "IDR", value: "idr" },
            { label: "Deterministic Graph", value: "deterministic-graph" },
            { label: "Cluster Graph", value: "cluster-graph" },
            { label: "Deterministic vs Cluster Graph", value: "deterministic-and-cluster-graph" }
        ];
        /* A client with overrides sees only the sources its graph has; the first one is the default. A list
           that matches nothing (a typo, a renamed source) falls back to all of them. */
        const clientSources = overrides?.graphReportDataSources
            ? allSources.filter(o => overrides.graphReportDataSources.includes(o.value))
            : [];
        const sources = clientSources.length ? clientSources : allSources;
        return {
            topNentitiesLoading: false,
            histogramLoading: false,
            histogramPerTypeLoading: false,
            edgesSummaryLoading: false,
            entitiesSummaryLoading: false,
            graphDiffLoading: false,
            expiredEntitiesLoading: false,
            expiredEdgesLoading: false,
            dataSource: sources[0].value,
            dataSourceOptions: sources,
            startDate: defaultDates?.start || dayjs().startOf('month').format("YYYY-MM-DD"),
            endDate: defaultDates?.end || dayjs().format("YYYY-MM-DD"),
            dateConfig: {
                wrap: true,
                dateFormat: 'Y-m-d',
                maxDate: new Date()
            },

            topNentitiesChartData: null,
            topNentitiesReportData: null,
            topNentitiesReportColumns: null,
            topNentitiesTableGroupBy: null,
            topNentitiesTableAggregateCol: null,
            topNentitiesTableCollapsibleCol: null,

            histogramChartData: null,
            histogramReportData: null,
            histogramReportColumns: null,
            histogramTableGroupBy: null,
            histogramTableAggregateCol: null,
            histogramTableCollapsibleCol: null,

            histogramPerTypeChartData: null,
            histogramPerTypeReportData: null,
            histogramPerTypeReportColumns: null,
            histogramPerTypeTableGroupBy: null,
            histogramPerTypeTableAggregateCol: null,
            histogramPerTypeTableCollapsibleCol: null,

            edgesSummaryChartData: null,
            edgesSummaryReportData: null,
            edgesSummaryReportColumns: null,
            edgesSummaryTableGroupBy: null,
            edgesSummaryTableAggregateCol: null,
            edgesSummaryTableCollapsibleCol: null,

            entitiesSummaryChartData: null,
            entitiesSummaryReportData: null,
            entitiesSummaryReportColumns: null,
            entitiesSummaryTableGroupBy: null,
            entitiesSummaryTableAggregateCol: null,
            entitiesSummaryTableCollapsibleCol: null,

            graphDiffChartData: null,
            graphDiffReportData: null,
            graphDiffReportColumns: null,
            graphDiffTableGroupBy: null,
            graphDiffTableAggregateCol: null,
            graphDiffTableCollapsibleCol: null,

            expiredEntitiesChartData: null,
            expiredEntitiesReportData: null,
            expiredEntitiesReportColumns: null,
            expiredEntitiesTableGroupBy: null,
            expiredEntitiesTableAggregateCol: null,
            expiredEntitiesTableCollapsibleCol: null,

            expiredEdgesChartData: null,
            expiredEdgesReportData: null,
            expiredEdgesReportColumns: null,
            expiredEdgesTableGroupBy: null,
            expiredEdgesTableAggregateCol: null,
            expiredEdgesTableCollapsibleCol: null,

            chartOptions: {
                maintainAspectRatio: false,
                responsive: true,
                plugins: {
                    colors: {
                        forceOverride: true
                    },
                    legend: {
                        display: true,
                        position: 'top',
                        maxHeight: 100
                    }
                },
                scales: {
                    y: {
                        type: 'linear',
                        display: true,
                        beginAtZero: true,
                        title: {
                            display: true,
                            text: 'Count'
                        }
                    },
                    y1: {
                        type: 'linear',
                        display: true,
                        position: 'right',
                        beginAtZero: true,
                        title: {
                            display: true,
                            text: 'Entity Count'
                        },
                        grid: {
                            drawOnChartArea: false,
                        },
                    },
                    x: {
                        title: {
                            display: true,
                            text: 'Date'
                        }
                    }
                },
                interaction: {
                    mode: 'index',
                    intersect: false,
                },
                elements: {
                    point: {
                        radius: 3,
                        hoverRadius: 5
                    }
                }
            },
        }
    },
    computed: {
        ...mapState({
            cid: (state) => state.currentClient.cid,
            activeTab: (state) => state.activeTab,
        }),
        tabItems() {
            const tabs = [
                { name: "Top N Entities", id: "id1", slot: "top_n_entities" },
                { name: "Histogram", id: "id2", slot: "histogram" },
                { name: "Histogram per Type", id: "id3", slot: "histogram_per_type" },
                { name: "Edges Summary", id: "id4", slot: "edges_summary" },
                { name: "Entities Summary", id: "id5", slot: "entities_summary" },
                { name: "Graph Diff", id: "id6", slot: "graph_diff" },
                { name: "Expired Entities", id: "id7", slot: "expired_entities"},
                { name: "Expired Edges", id: "id8", slot: "expired_edges"}
            ];
            const metricsBySource = {
                "idr": ["Top N Entities", "Histogram", "Edges Summary", "Entities Summary"],
                "deterministic-graph": ["Edges Summary", "Top N Entities", "Graph Diff", "Expired Entities", "Expired Edges"],
                "cluster-graph": ["Top N Entities", "Histogram", "Histogram per Type", "Graph Diff", "Expired Entities"],
                "deterministic-and-cluster-graph": ["Graph Diff"]
            };
            const allowed = metricsBySource[this.dataSource] || [];
            /* A client override can drop tabs whose report came back empty. Hidden while loading
               too, so a tab appears once it has rows rather than showing and then vanishing. */
            const hideWhenEmpty = getAgmClientOverrides(this.$route.params.cid || this.$store.state.currentClient?.cid)?.graphReportTabsHiddenWhenEmpty || [];
            const hasRows = {
                "Graph Diff": !this.graphDiffLoading && !!this.graphDiffReportData?.length,
                "Expired Entities": !this.expiredEntitiesLoading && !!this.expiredEntitiesReportData?.length,
                "Expired Edges": !this.expiredEdgesLoading && !!this.expiredEdgesReportData?.length,
            };
            return tabs.filter(tab => allowed.includes(tab.name)
                && !(hideWhenEmpty.includes(tab.name) && !hasRows[tab.name]));
        }
    },
    watch: {
        startDate(newVal) {
            this.refreshReport();
        },
        endDate(newVal) {
            this.refreshReport();
        },
        dataSource(newVal) {
            this.refreshReport();
        },
    },
    methods: {
        generateNDaysChartsByGraph(reportData) {
            const chartsByGraph = {};
            if (!reportData) return {};

            // First group by graph_name
            const graphGroups = {};
            reportData.forEach(item => {
                const graphName = item.graph_name || 'Unknown Graph';
                if (!graphGroups[graphName]) {
                    graphGroups[graphName] = [];
                }
                graphGroups[graphName].push(item);
            });

            // For each graph, create a chart with individual entities
            Object.keys(graphGroups).forEach(graphName => {
                const graphData = graphGroups[graphName];

                // Group by date within this graph
                const dateGroups = {};
                graphData.forEach(item => {
                    if (item.event_date) {
                        // Format date from YYYYMMDD to YYYY-MM-DD
                        const dateStr = item.event_date.toString();
                        const formattedDate = `${dateStr.substring(0, 4)}-${dateStr.substring(4, 6)}-${dateStr.substring(6, 8)}`;

                        if (!dateGroups[formattedDate]) {
                            dateGroups[formattedDate] = [];
                        }
                        dateGroups[formattedDate].push(item);
                    }
                });

                // Sort dates
                const sortedDates = Object.keys(dateGroups).sort();

                let datasets = [];

                // Check if we have entity_id (for individual entity tracking) or aggregate data
                const hasEntityIds = graphData.some(item => item.entity_id);

                if (hasEntityIds) {
                    // Get all unique entities for this graph
                    const entityIds = [...new Set(graphData.map(item => item.entity_id))];

                    // Create datasets for each entity
                    datasets = entityIds.slice(0, 20).map((entityId, index) => { // Limit to 20 entities for readability
                        const entityData = sortedDates.map(date => {
                            const dayData = dateGroups[date] || [];
                            const entityRecord = dayData.find(item => item.entity_id === entityId);
                            return entityRecord ? parseInt(entityRecord.neighbor_count) || 0 : null;
                        });

                        // Generate a color for this entity
                        const hue = (index * 137.508) % 360; // Golden angle approximation for good color distribution
                        const color = `hsl(${hue}, 70%, 50%)`;

                        return {
                            label: `Entity ${entityId.substring(0, 8)}...`, // Truncate for readability
                            data: entityData,
                            borderColor: color,
                            backgroundColor: color + '20', // Add transparency
                            fill: false,
                            tension: 0.1,
                            spanGaps: true // Connect points even when there are null values
                        };
                    });
                } else {
                    // For aggregate data (like histogram), show totals per date
                    const neighborCountData = [];
                    const entityCountData = [];
                    const edgesCountData = [];

                    sortedDates.forEach(date => {
                        const dayData = dateGroups[date] || [];
                        const totalNeighborCount = dayData.reduce((sum, item) => sum + (parseInt(item.neighbor_count) || 0), 0);
                        const totalEntityCount = dayData.reduce((sum, item) => sum + (parseInt(item.entity_count) || 0), 0);
                        const totalEdgesCount = dayData.reduce((sum, item) => sum + (parseInt(item.edges_count) || 0), 0);

                        neighborCountData.push(totalNeighborCount);
                        entityCountData.push(totalEntityCount);
                        edgesCountData.push(totalEdgesCount)
                    });

                    datasets = [
                        {
                            label: 'Total Neighbor Count',
                            data: neighborCountData,
                            borderColor: 'rgba(54, 162, 235, 1)',
                            backgroundColor: 'rgba(54, 162, 235, 0.1)',
                            fill: false,
                            tension: 0.1
                        }
                    ];

                    // Add entity count dataset if it has meaningful data
                    if (entityCountData.some(count => count > 0)) {
                        datasets.push({
                            label: 'Total Entity Count',
                            data: entityCountData,
                            borderColor: 'rgba(255, 99, 132, 1)',
                            backgroundColor: 'rgba(255, 99, 132, 0.1)',
                            fill: false,
                            tension: 0.1,
                            yAxisID: 'y1'
                        });
                    }
                    // Add edges count dataset if it has meaningful data
                    if (edgesCountData.some(count => count > 0)) {
                        datasets.push({
                            label: 'Total Edges Count',
                            data: edgesCountData,
                            borderColor: 'rgba(255, 99, 132, 1)',
                            backgroundColor: 'rgba(255, 99, 132, 0.1)',
                            fill: false,
                            tension: 0.1,
                            yAxisID: 'y1'
                        });
                    }
                }

                chartsByGraph[graphName] = {
                    labels: sortedDates,
                    datasets: datasets
                };
            });

            return chartsByGraph;
        },
        formatHeaderTitle(header) {
            // Convert snake_case to Title Case
            return header.split('_')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
        },
        configureCollapsibleTable(headers, reportType) {
            let tableGroupBy, tableAggregateCol, tableCollapsibleCol;

            // Set up groupBy based on report type and available headers
            if (reportType === 'histogram') {
                // For histogram, group by graph_name and job_name if available
                const possibleGroupBy = ['event_date', 'tenant_id', 'graph_name', 'job_name'];
                tableGroupBy = possibleGroupBy.filter(col => headers.includes(col));

                // Use entity_count for histogram aggregation if available, otherwise neighbor_count
                if (headers.includes('entity_count')) {
                    tableAggregateCol = 'entity_count';
                } else if (headers.includes('neighbor_count')) {
                    tableAggregateCol = 'neighbor_count';
                }
            } else if (reportType === 'top_n_entities') {
                // For top entities, group by graph_name and entity_type/entity_domain
                const possibleGroupBy = ['event_date', 'tenant_id', 'graph_name', 'job_name'];
                tableGroupBy = possibleGroupBy.filter(col => headers.includes(col));
                tableAggregateCol = 'neighbor_count';
            } else {
                // Generic configuration for other report types
                const possibleGroupBy = ['event_date', 'tenant_id', 'graph_name', 'job_name'];
                tableGroupBy = possibleGroupBy.filter(col => headers.includes(col));

                // Find best numeric column for aggregation
                if (headers.includes('neighbor_count')) {
                    tableAggregateCol = 'neighbor_count';
                } else if (headers.includes('entity_count')) {
                    tableAggregateCol = 'entity_count';
                } else {
                    const numericCol = headers.find(header =>
                        header.includes('count') || header.includes('value') || header.includes('num')
                    );
                    tableAggregateCol = numericCol || headers[headers.length - 1];
                }
            }

            // Set collapsible column (first groupBy column)
            if (tableGroupBy.length > 0) {
                tableCollapsibleCol = tableGroupBy[0];
            } else if (headers.includes('graph_name')) {
                tableCollapsibleCol = 'graph_name';
                tableGroupBy = ['graph_name']; // Ensure at least one groupBy column
            } else {
                tableCollapsibleCol = headers[0];
                tableGroupBy = [headers[0]]; // Ensure at least one groupBy column
            }

            return { tableGroupBy, tableAggregateCol, tableCollapsibleCol };
        },
        async executeQuery(report, args) {
            return axios.post(this.$store.state.countsUrl + `/v2/query/${report}_date_range/exec`, { "arguments": args }, {
                headers: {
                    Authorization: `Bearer ${this.$store.state.accessToken}`,
                }
            })
        },
        getPayloadArguments(report) {
            let args = {
                cid: this.cid,
                start_date: this.startDate.replace(/-/g, ''),
                end_date: this.endDate.replace(/-/g, '')
            }
            if (report != "histogram_per_type" && report != "entities_summary" && report != "expired_edges") args["data_type"] = this.dataSource
            return args
        },
        getDataObject(data, headers) {
            // Transform data into objects for the table
            if (!data) return null;
            return data.map(row => {
                const obj = {};
                headers.forEach((header, index) => {
                    // Convert numeric fields to numbers
                    if (header === 'neighbor_count' || header === 'entity_count' ||
                        header.includes('count') || header.includes('value') || header.includes('num')) {
                        obj[header] = parseFloat(row[index]) || 0;
                    } else {
                        obj[header] = row[index];
                    }
                });
                return obj;
            });
        },
        processResponse(data, headers, reportType) {
            let reportData, reportColumns;
            reportData = this.getDataObject(data, headers);
            reportColumns = headers.map(header => ({
                data: header,
                title: this.formatHeaderTitle(header)
            }));

            // Set up collapsible table configuration based on available headers
            let { tableGroupBy, tableAggregateCol, tableCollapsibleCol } = this.configureCollapsibleTable(headers, reportType);

            // Final validation - ensure we have minimum required config
            if (!tableGroupBy.length) {
                tableGroupBy = [headers[0]];
                tableCollapsibleCol = headers[0];
            }
            if (!tableAggregateCol) tableAggregateCol = headers[headers.length - 1];
            return { reportData, reportColumns, tableGroupBy, tableAggregateCol, tableCollapsibleCol }
        },
        async getTopNentitiesReport() {
            if (this.dataSource == "deterministic-and-cluster-graph") return;
            this.topNentitiesLoading = true;
            const args = this.getPayloadArguments("top_n_entities");
            await this.executeQuery("top_n_entities", args).then(response => {
                if (response.data?.data) {
                    const { data, headers } = response.data.data;
                    const { reportData, reportColumns, tableGroupBy, tableAggregateCol, tableCollapsibleCol } = this.processResponse(data, headers, "top_n_entities")

                    this.topNentitiesReportData = reportData;
                    this.topNentitiesReportColumns = reportColumns;
                    this.topNentitiesTableGroupBy = tableGroupBy;
                    this.topNentitiesTableAggregateCol = tableAggregateCol;
                    this.topNentitiesTableCollapsibleCol = tableCollapsibleCol;

                    this.topNentitiesChartData = this.generateNDaysChartsByGraph(reportData);
                }
            }).catch(error => {
                let err = error.response?.data.error ? error.response.data.error : error.message;
                console.error("Error in getting 'Top N Entities' report: ", err)
            }).finally(() => {
                this.topNentitiesLoading = false;
            })
        },
        async getHistogramReport() {
            if (this.dataSource == "deterministic-graph" || this.dataSource == "deterministic-and-cluster-graph") return;
            this.histogramLoading = true;
            const args = this.getPayloadArguments("histogram");
            await this.executeQuery("histogram", args).then(response => {
                if (response.data?.data) {
                    const { data, headers } = response.data.data;
                    const { reportData, reportColumns, tableGroupBy, tableAggregateCol, tableCollapsibleCol } = this.processResponse(data, headers, "histogram")

                    this.histogramReportData = reportData;
                    this.histogramReportColumns = reportColumns;
                    this.histogramTableGroupBy = tableGroupBy;
                    this.histogramTableAggregateCol = tableAggregateCol;
                    this.histogramTableCollapsibleCol = tableCollapsibleCol;

                    this.histogramChartData = this.generateNDaysChartsByGraph(reportData);
                }
            }).catch(error => {
                let err = error.response?.data.error ? error.response.data.error : error.message;
                console.error("Error in getting 'Histogram' report: ", err)
            }).finally(() => {
                this.histogramLoading = false;
            })
        },
        async getHistogramPerTypeReport() {
            if (this.dataSource != "cluster-graph") return;
            this.histogramPerTypeLoading = true;
            const args = this.getPayloadArguments("histogram_per_type");
            await this.executeQuery("histogram_per_type", args).then(response => {
                if (response.data?.data) {
                    const { data, headers } = response.data.data;
                    const { reportData, reportColumns, tableGroupBy, tableAggregateCol, tableCollapsibleCol } = this.processResponse(data, headers, "histogram_per_type")

                    this.histogramPerTypeReportData = reportData;
                    this.histogramPerTypeReportColumns = reportColumns;
                    this.histogramPerTypeTableGroupBy = tableGroupBy;
                    this.histogramPerTypeTableAggregateCol = tableAggregateCol;
                    this.histogramPerTypeTableCollapsibleCol = tableCollapsibleCol;

                    this.histogramPerTypeChartData = this.generateNDaysChartsByGraph(reportData);
                }
            }).catch(error => {
                let err = error.response?.data.error ? error.response.data.error : error.message;
                console.error("Error in getting 'Histogram Per Type' report: ", err)
            }).finally(() => {
                this.histogramPerTypeLoading = false;
            })
        },
        async getEdgesSummaryReport() {
            if (this.dataSource == "cluster-graph" || this.dataSource == "deterministic-and-cluster-graph") return;
            this.edgesSummaryLoading = true;
            const args = this.getPayloadArguments("edges_summary");
            await this.executeQuery("edges_summary", args).then(response => {
                if (response.data?.data) {
                    const { data, headers } = response.data.data;
                    const { reportData, reportColumns, tableGroupBy, tableAggregateCol, tableCollapsibleCol } = this.processResponse(data, headers, "edges_summary")

                    this.edgesSummaryReportData = reportData;
                    this.edgesSummaryReportColumns = reportColumns;
                    this.edgesSummaryTableGroupBy = tableGroupBy;
                    this.edgesSummaryTableAggregateCol = tableAggregateCol;
                    this.edgesSummaryTableCollapsibleCol = tableCollapsibleCol;

                    this.edgesSummaryChartData = this.generateNDaysChartsByGraph(reportData);
                }
            }).catch(error => {
                let err = error.response?.data.error ? error.response.data.error : error.message;
                console.error("Error in getting 'Edges Summary' report: ", err)
            }).finally(() => {
                this.edgesSummaryLoading = false;
            })
        },
        async getEntitiesSummaryReport() {
            if (this.dataSource != "idr") return;
            this.entitiesSummaryLoading = true;
            const args = this.getPayloadArguments("entities_summary");
            await this.executeQuery("entities_summary", args).then(response => {
                if (response.data?.data) {
                    const { data, headers } = response.data.data;
                    const { reportData, reportColumns, tableGroupBy, tableAggregateCol, tableCollapsibleCol } = this.processResponse(data, headers, "entities_summary")

                    this.entitiesSummaryReportData = reportData;
                    this.entitiesSummaryReportColumns = reportColumns;
                    this.entitiesSummaryTableGroupBy = tableGroupBy;
                    this.entitiesSummaryTableAggregateCol = tableAggregateCol;
                    this.entitiesSummaryTableCollapsibleCol = tableCollapsibleCol;

                    this.entitiesSummaryChartData = this.generateNDaysChartsByGraph(reportData);
                }
            }).catch(error => {
                let err = error.response?.data.error ? error.response.data.error : error.message;
                console.error("Error in getting 'Entities Summary' report: ", err)
            }).finally(() => {
                this.entitiesSummaryLoading = false;
            })
        },
        async getGraphDiffReport() {
            if (this.dataSource == "idr") return;
            this.graphDiffLoading = true;
            const args = this.getPayloadArguments("graph_diff");
            await this.executeQuery("graph_diff", args).then(response => {
                if (response.data?.data) {
                    const { data, headers } = response.data.data;
                    const { reportData, reportColumns, tableGroupBy, tableAggregateCol, tableCollapsibleCol } = this.processResponse(data, headers, "graph_diff")

                    this.graphDiffReportData = reportData;
                    this.graphDiffReportColumns = reportColumns;
                    this.graphDiffTableGroupBy = tableGroupBy;
                    this.graphDiffTableAggregateCol = tableAggregateCol;
                    this.graphDiffTableCollapsibleCol = tableCollapsibleCol;

                    this.graphDiffChartData = this.generateNDaysChartsByGraph(reportData);
                }
            }).catch(error => {
                let err = error.response?.data.error ? error.response.data.error : error.message;
                console.error("Error in getting 'Graph Diff' report: ", err)
            }).finally(() => {
                this.graphDiffLoading = false;
            })
        },
        async getExpiredEntitiesReport() {
            if (this.dataSource == "idr" || this.dataSource == "deterministic-and-cluster-graph") return;
            this.expiredEntitiesLoading = true;
            const args = this.getPayloadArguments("expired_entities");
            await this.executeQuery("expired_entities", args).then(response => {
                if (response.data?.data) {
                    const { data, headers } = response.data.data;
                    const { reportData, reportColumns, tableGroupBy, tableAggregateCol, tableCollapsibleCol } = this.processResponse(data, headers, "expired_entities")

                    this.expiredEntitiesReportData = reportData;
                    this.expiredEntitiesReportColumns = reportColumns;
                    this.expiredEntitiesTableGroupBy = tableGroupBy;
                    this.expiredEntitiesTableAggregateCol = tableAggregateCol;
                    this.expiredEntitiesTableCollapsibleCol = tableCollapsibleCol;

                    this.expiredEntitiesChartData = this.generateNDaysChartsByGraph(reportData);
                }
            }).catch(error => {
                let err = error.response?.data.error ? error.response.data.error : error.message;
                console.error("Error in getting 'Expired Entities' report: ", err)
            }).finally(() => {
                this.expiredEntitiesLoading = false;
            })
        },
        async getExpiredEdgesReport() {
            if (this.dataSource != "deterministic-graph") return;
            this.expiredEdgesLoading = true;
            const args = this.getPayloadArguments("expired_edges");
            await this.executeQuery("expired_edges", args).then(response => {
                if (response.data?.data) {
                    const { data, headers } = response.data.data;
                    const { reportData, reportColumns, tableGroupBy, tableAggregateCol, tableCollapsibleCol } = this.processResponse(data, headers, "expired_edges")

                    this.expiredEdgesReportData = reportData;
                    this.expiredEdgesReportColumns = reportColumns;
                    this.expiredEdgesTableGroupBy = tableGroupBy;
                    this.expiredEdgesTableAggregateCol = tableAggregateCol;
                    this.expiredEdgesTableCollapsibleCol = tableCollapsibleCol;

                    this.expiredEdgesChartData = this.generateNDaysChartsByGraph(reportData);
                }
            }).catch(error => {
                let err = error.response?.data.error ? error.response.data.error : error.message;
                console.error("Error in getting 'Expired Edges' report: ", err)
            }).finally(() => {
                this.expiredEdgesLoading = false;
            })
        },
        async refreshReport() {
            this.getTopNentitiesReport();
            this.getHistogramReport();
            this.getHistogramPerTypeReport();
            this.getEdgesSummaryReport();
            this.getEntitiesSummaryReport();
            this.getGraphDiffReport();
            this.getExpiredEntitiesReport();
            this.getExpiredEdgesReport();
        },
        getCurrentTabReportData(reportType) {
            if (reportType == "top_n_entities") return this.topNentitiesReportData;
            else if (reportType == "histogram") return this.histogramReportData;
            else if (reportType == "histogram_per_type") return this.histogramPerTypeReportData;
            else if (reportType == "edges_summary") return this.edgesSummaryReportData;
            else if (reportType == "entities_summary") return this.entitiesSummaryReportData;
            else if (reportType == "graph_diff") return this.graphDiffReportData;
            else if (reportType == "expired_entities") return this.expiredEntitiesReportData;
            else return [];
        },
        exportCSV() {
            const reportType = this.activeTab.toLowerCase().replace(/ /g, "_");
            let reportData = this.getCurrentTabReportData(reportType)
            if (reportData?.length > 0) {
                const title = `graph-report-${reportType}-(${this.startDate}_to_${this.endDate})`;
                csv.exportCSV(title, reportData);
            } else {
                console.error("No data to export")
                let toastOptions = {
                    message: "No data to export",
                    type: "error",
                };
                this.$store.dispatch("addToast", toastOptions);
            }
        },
    },
    async beforeMount() {
        this.refreshReport();
    }
}
</script>
<style scoped>
.table-container {
    overflow-x: auto;
    width: 100%;
}

.chart-view {
    height: 40vh !important;
}
</style>